import PropTypes from 'prop-types';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

/**
 * The result of checking an uploaded topic (backend adminMicroSimController.checkUpload):
 *   - conversion errors — the workbook itself could not be read (sheet + row);
 *   - errors   — content rules from the validator (block saving);
 *   - warnings — worth a look, but the topic can still be saved.
 */
const Row = ({ icon: Icon, tone, title, detail }) => (
  <li className="flex gap-2 py-1.5 text-sm">
    <Icon size={15} className={`mt-0.5 shrink-0 ${tone}`} />
    <span className="min-w-0">
      <span className="text-neutral-800 dark:text-neutral-200">{title}</span>
      {detail && <span className="block break-words font-mono text-[11px] text-neutral-500">{detail}</span>}
    </span>
  </li>
);
Row.propTypes = { icon: PropTypes.elementType.isRequired, tone: PropTypes.string, title: PropTypes.node, detail: PropTypes.node };

const MicroSimValidationReport = ({ report }) => {
  if (!report) return null;
  const { conversionErrors = [], errors = [], warnings = [], summary } = report;
  const blocking = conversionErrors.length + errors.length;

  return (
    <div className="space-y-3">
      <div className={`flex items-start gap-2 rounded-xl px-3.5 py-3 text-sm ${blocking ? 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-200' : 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200'}`}>
        {blocking ? <XCircle size={17} className="mt-0.5 shrink-0" /> : <CheckCircle2 size={17} className="mt-0.5 shrink-0" />}
        <div>
          <p className="font-semibold">
            {blocking ? `${blocking} problem${blocking === 1 ? '' : 's'} to fix before saving` : 'Ready to save'}
          </p>
          {summary && (
            <p className="mt-0.5 text-xs opacity-90">
              {summary.title || 'Untitled'} ({summary.slug || 'no slug'}) · {summary.isNewTopic ? 'new topic' : `new version ${summary.nextVersion}`}
              {' · '}{summary.rounds} rounds · {summary.decisions} decisions · {summary.skills} skills · {summary.industries} industries
            </p>
          )}
        </div>
      </div>

      {(conversionErrors.length > 0 || errors.length > 0) && (
        <ul className="divide-y divide-neutral-100 rounded-xl border border-neutral-200 px-3 dark:divide-neutral-800 dark:border-neutral-700">
          {conversionErrors.map((e, i) => (
            <Row
              key={`c${i}`}
              icon={XCircle}
              tone="text-red-500"
              title={e.message}
              detail={e.sheet ? `Sheet “${e.sheet}”${e.row ? `, row ${e.row}` : ''}` : null}
            />
          ))}
          {errors.map((e, i) => (
            <Row key={`e${i}`} icon={XCircle} tone="text-red-500" title={e.message} detail={`${e.code} · ${e.path}`} />
          ))}
        </ul>
      )}

      {warnings.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">Warnings (you can still save)</p>
          <ul className="divide-y divide-neutral-100 rounded-xl border border-amber-200 px-3 dark:divide-neutral-800">
            {warnings.map((w, i) => (
              <Row key={`w${i}`} icon={AlertTriangle} tone="text-amber-500" title={w.message} detail={`${w.code} · ${w.path}`} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

MicroSimValidationReport.propTypes = { report: PropTypes.object };

export default MicroSimValidationReport;
